import { Link } from 'react-router-dom';
import type { Work } from '@/mocks/catalog';
import { typeLabel } from '@/mocks/catalog';

const playInfo = [
  { icon: 'ri-play-circle-line', label: '观看方式', value: '正版平台在线观看' },
  { icon: 'ri-hd-line', label: '画面规格', value: '1080P / 4K HDR（视平台而定）' },
  { icon: 'ri-volume-up-line', label: '音轨', value: '原声 + 中文字幕' },
  { icon: 'ri-cloud-line', label: '更新状态', value: '以版权方平台排期为准' },
];

export default function WorkInfoPanel({ work }: { work: Work }) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.55fr_1fr]">
      <article className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
        <h2 className="font-display text-lg font-bold text-foreground-950">剧情简介</h2>
        <p className="mt-3 text-sm leading-7 text-foreground-600">{work.synopsis}</p>
        <p className="mt-3 text-sm leading-7 text-foreground-600">
          本页内容为作品介绍与资源索引，包括主创信息、题材标签与观看渠道说明。我们希望在不剧透关键转折的前提下，帮你在开始观看前判断这部作品是否适合此刻的你。
        </p>

        <div className="mt-6 border-t border-background-200/70 pt-5">
          <h2 className="font-display text-lg font-bold text-foreground-950">主创信息</h2>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-md bg-background-100 p-3">
              <p className="text-[11px] uppercase tracking-wider text-foreground-400">导演</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">{work.director}</p>
            </div>
            <div className="rounded-md bg-background-100 p-3">
              <p className="text-[11px] uppercase tracking-wider text-foreground-400">主演 / 主要嘉宾</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">{work.cast.join('、')}</p>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t border-background-200/70 pt-5">
          <h2 className="font-display text-lg font-bold text-foreground-950">题材标签</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {[...work.genres, ...work.tags].map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1 text-xs text-secondary-900 transition-colors hover:border-accent-400 hover:text-accent-700"
              >
                #{tag}
              </Link>
            ))}
            <Link
              to={`/category/${work.type}`}
              className="rounded-full bg-primary-950 px-3 py-1 text-xs text-background-50 transition-colors hover:bg-primary-800"
            >
              {typeLabel[work.type]}频道
            </Link>
          </div>
        </div>
      </article>

      <div className="flex flex-col gap-4">
        <section className="rounded-lg border border-background-200/70 bg-background-50 p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-bold text-foreground-950">观看与资源说明</h2>
            <span className="rounded-md bg-accent-100 px-2 py-0.5 text-[11px] font-medium text-accent-800">信息说明</span>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-foreground-500">
            本站不提供播放器或下载入口，以下信息仅用于说明作品的常见观看方式，具体以版权方平台页面为准。
          </p>
          <dl className="mt-4 space-y-2.5">
            {playInfo.map((item) => (
              <div key={item.label} className="flex items-start gap-2.5 rounded-md bg-background-100 px-3 py-2.5">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-50 text-accent-700">
                  <i className={`${item.icon} text-sm`}></i>
                </span>
                <div className="min-w-0">
                  <dt className="text-[11px] text-foreground-400">{item.label}</dt>
                  <dd className="text-xs font-medium text-foreground-800">{item.value}</dd>
                </div>
              </div>
            ))}
          </dl>
          <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-foreground-400">
            <i className="ri-shield-check-line mt-0.5 text-accent-600"></i>
            请通过正规渠道观看，尊重创作者与版权方。
          </p>
        </section>

        <section className="rounded-lg bg-primary-950 p-5 text-background-100">
          <h2 className="font-display text-base font-bold text-white">作品档案</h2>
          <dl className="mt-3 space-y-2 text-xs">
            {[
              { label: '类型', value: typeLabel[work.type] },
              { label: '上映 / 上线', value: `${work.year} 年` },
              { label: '地区', value: work.region },
              { label: work.type === 'tv' || work.type === 'show' ? '集数' : '片长', value: work.duration },
              { label: '热度', value: `${work.views} 次关注` },
              { label: '评分', value: `${work.rating.toFixed(1)} / 10` },
            ].map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-3 border-b border-white/10 pb-2 last:border-b-0 last:pb-0">
                <dt className="text-background-400">{row.label}</dt>
                <dd className="text-right text-background-100">{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </div>
  );
}